import { identity, omit } from 'lodash-es';
import qs from 'query-string';
import { PLAN_SERVICE_BASEURL, getEnvironmentConfigValue } from 'config';
import ServiceFactory, { returnVoid } from 'services/ServiceFactory';
import {
  ACCEPT_PROMOTION_PATH,
  ALL_PLANS_PATH,
  CHECKOUT_REDIRECTOR_PATH,
  MY_CHECKOUT_URLS_PATH,
  CUSTOMER_PATH,
  EXTERNAL_PAYMENT_PORTAL_LINK_PATH,
  MY_COUPONS_PATH,
  MY_REFERRAL_OWNER_PATH,
  MY_SUBSCRIPTION_PATH,
  PAYMENT_METHOD_PATH,
  PODCAST_PROMO_CREDITS_PATH,
  PROMOTION_INFO_PATH,
  REFERRAL_EMAIL_INVITATION_PATH,
} from './constants';
import {
  AcceptPromotionRequest,
  AcceptPromotionResponse,
  CouponsResponse,
  CreateMyCheckoutUrlsArgs,
  CreateMyCheckoutUrlsResponse,
  CreateCheckoutUrlArgs,
  CreatePaymentMethodArgs,
  Customer,
  ExternalPaymentPortalLinkRequest,
  ExternalPaymentPortalLinkResponse,
  GetAllPlansArgs,
  GetCustomerArgs,
  GetPromotionArgs,
  GetUserSubscriptionArgs,
  PlanList,
  PodcastPromoCreditsResponse,
  Promotion,
  ReferralOwner,
  SendReferralEmailInvitationsArgs,
  SubscriptionUpdateRequest,
  UserSubscription,
  CreateMyCheckoutUrlArgs,
} from './types';

const serviceFactory = new ServiceFactory(PLAN_SERVICE_BASEURL);

/**
 * Get all plans
 */
export const getPlans = serviceFactory.createGetEndpoint(ALL_PLANS_PATH)<
  GetAllPlansArgs,
  PlanList
>({
  queryParams: identity,
});

/**
 * Get user's subscription
 */
export const getMySubscription = serviceFactory.createGetEndpoint(
  MY_SUBSCRIPTION_PATH,
)<GetUserSubscriptionArgs, UserSubscription>({
  queryParams: identity,
});

/**
 * Update user's subscription
 * 
 * If the desired target subscription has a cost associated with it,
 * this endpoint will attempt to map the user id to a stripe customer
 * identifier. If the user does not have a customer identifier, this
 * will throw an error.

 * For updating special subscriptions, such as enterprise and API
 * integrators, this endpoint should not be used. This endpoint
 * only allows editing of subscriptions that are editable by the user.
 *
 * WARNING: making updates to this endpoint has cost ramifications. If
 * the user was subscribed to a pro monthly, and the user’s credit card
 * was on file, this endpoint WILL trigger the payment and automatically
 * subscribe the user to the plan.
 * 
 * Any one or all fields can be specified (subscribeTo, autoRenewal or
 * forcedCancellationNotice).
 * 
 * The autoRenewal field specifies whether the plan should be renewed or
 * cancelled at the end of the billing cycle. For example, sending
 * ‘autoRenewal = false’ without specifying subscribeTo will result in
 * the user’s current plan being cancelled (although this won’t take
 * effect until the end of the billing cycle). If the plan is currently
 * paused, it will be cancelled immediately. If the plan is scheduled to
 * be paused, the pause will be cancelled.
 * The forcedCancellationNotice field specifies if the user should be
 * notified if their previous subscription was forcefully cancelled. Once
 * the user acknowledges this in the app, this endpoint should be used to
 * let the backend know that the user has been notified.
 * 
 * For the following fields (pausePlanDurationMonths, cancelPause),
 * no other fields should be sent with them to avoid unintended side
 * effects.
 */
export const updateMySubscription = serviceFactory.createPutEndpoint(
  MY_SUBSCRIPTION_PATH,
)<SubscriptionUpdateRequest, void>({
  data: identity,
});

/**
 * Create payment method for a user
 *
 * A payment method can be assigned for user using either the stripeToken
 * or the paymentMethodId. If both are sent, stripeToken will be
 * prioritized.
 *
 * The payment method submitted as part of this request will be set as the
 * default payment method for the user.
 *
 * This endpoint will also attempt to resolve all open invoices.
 */
export const createPaymentMethod = serviceFactory.createPostEndpoint(
  PAYMENT_METHOD_PATH,
)({
  data: (args: CreatePaymentMethodArgs) => omit(args, 'userId'),
  select: returnVoid(),
});

/**
 * Retrieve customer info for user
 */
export const getCustomer = serviceFactory.createGetEndpoint(CUSTOMER_PATH)<
  GetCustomerArgs,
  Customer
>();

/**
 * Get coupon offers for the user
 */
export const getMyCoupons = serviceFactory.createGetEndpoint(MY_COUPONS_PATH)<
  undefined,
  CouponsResponse
>();

/**
 * Get referral owner information for the user
 */
export const getMyReferralOwnerInfo = serviceFactory.createGetEndpoint(
  MY_REFERRAL_OWNER_PATH,
)<undefined, ReferralOwner>();

/**
 * Get podcast promo credits
 */
export const getMyPodcastPromoCredits = serviceFactory.createGetEndpoint(
  PODCAST_PROMO_CREDITS_PATH,
)<undefined, PodcastPromoCreditsResponse>();

/**
 * Create referral email invitation
 */
export const sendReferralEmailInvitations = serviceFactory.createPostEndpoint(
  REFERRAL_EMAIL_INVITATION_PATH,
)({
  data(args: SendReferralEmailInvitationsArgs) {
    return {
      referralInvitations: args.emailAddresses.map((email) => ({ email })),
    };
  },
  select: returnVoid(),
});

/**
 * Create Link to External Portal
 *
 * The content of the portal is controlled via the stripe dashboard
 */
export const createPaymentServicePortalLink = serviceFactory.createPostEndpoint(
  EXTERNAL_PAYMENT_PORTAL_LINK_PATH,
)<ExternalPaymentPortalLinkRequest, ExternalPaymentPortalLinkResponse>({
  data: identity,
});

/**
 * Get promotion info
 */
export const getPromotion = serviceFactory.createGetEndpoint(
  PROMOTION_INFO_PATH,
)<GetPromotionArgs, Promotion>();

/**
 * Create promotion acceptance
 */
export const acceptPromotion = serviceFactory.createPostEndpoint(
  ACCEPT_PROMOTION_PATH,
)<AcceptPromotionRequest, AcceptPromotionResponse>({
  data: identity,
});

/**
 * Returns a URL pointing to an endpont on plan-service.
 *
 * The plan-service endpoint does the following:
 *
 * Builds the checkout url on the fly and redirects the user of this
 * endpoint to the stripe’s checkout page.
 */
export function createCheckoutUrl({
  application,
  targetPlan,
  userId,
}: CreateCheckoutUrlArgs) {
  const baseUrl = getEnvironmentConfigValue(PLAN_SERVICE_BASEURL);

  return qs.stringifyUrl({
    url: baseUrl + CHECKOUT_REDIRECTOR_PATH,
    query: {
      application,
      targetPlan,
      userId,
    },
  });
}

/**
 * This endpoint will generate checkout urls for all tiers. In order
 * to prevent the url from expiring, the urls are just references to
 * another endpoint on this service, that will create the url on the fly.
 */
export const createMyCheckoutUrls = serviceFactory.createPostEndpoint(
  MY_CHECKOUT_URLS_PATH,
)<CreateMyCheckoutUrlsArgs, CreateMyCheckoutUrlsResponse>({
  data: identity,
});

/**
 * Create a checkout URL for a specific plan.
 *
 * This is a convenience wrapper around `createMyCheckoutUrls` that returns
 * a single checkout URL for the specified target plan alias instead of
 * URLs for all available tiers.
 *
 * @returns the checkout URL for the specified plan, or undefined if the plan
 * was not found in the response.
 */
const createMyCheckoutUrlHandler = async ({
  application,
  targetPlanAlias,
  rewardfulReferral,
}: CreateMyCheckoutUrlArgs): Promise<string | undefined> => {
  const response = await createMyCheckoutUrls({
    application,
    includeAllPlans: true,
    rewardfulReferral,
  });

  if (response.data instanceof Array) {
    return response.data.find(
      ({ planToAlias }) => planToAlias === targetPlanAlias,
    )?.url;
  }

  return response.data?.url;
};

export const createMyCheckoutUrl = Object.assign(createMyCheckoutUrlHandler, {
  url: createMyCheckoutUrls.url,
});
